/* eslint-disable no-await-in-loop */
import * as Sentry from "@sentry/react";
import { logger } from "../../../utils/logger";
import { AxiosError } from "axios";
import { ethers } from "ethers";
import { supportedChainId } from "../../../utils/wallet/connectors";
import moment from "moment";
import { useCallback, useContext, useRef } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { LocalStorageKeyEnum } from "../../../enums/localStorage";
import { SIGNING_KEY_TTL } from "../../../constants/session";
import { ChainCodeErrorEnum } from "../../../enums/rpcErrorCodes";
import { registerSigningKeyErrorMessage } from "./registerErrorMessage";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { getChainConfigOrThrow } from "../../../config/chains";
import { IAuthInfo, IDecryptedAuthInfo } from "../../../interfaces/AuthInfo";
import { authApi } from "../../../services/api/apiFetcher";
import { nanosToMillis } from "../../../utils/date";
import { delay } from "../../../utils/delay";
import { verifyNewApiKey } from "./verifyNewApiKey";
import { IRegisterMessage, createSigningKey } from "../../../utils/signing";
import { ToastStatusEnum } from "../../../utils/toast";
import useWallet from "../../wallet/useWallet";

type IRegisterAuthInfoResponse =
  | {
      authInfo: undefined;
      error: string;
    }
  | {
      authInfo: IDecryptedAuthInfo;
      error: undefined;
    };

const signingKeyRegistrationInFlightAccounts = new Set<string>();

export const useRegister = () => {
  // `provider` deliberately NOT taken from here: useWallet() returns
  // web3-react's PRIORITY connector, which is not necessarily the wallet the
  // user selected. registerSigningKey resolves its own via activeSigner.
  const { account } = useWallet();
  const { enableTrading } = useContext(AuthContext);
  const registerSigningKeyInFlightRef = useRef(false);
  const fetcher = authApi();
  const referralCode = window.localStorage.getItem(
    LocalStorageKeyEnum.REFERRAL_CODE
  );

  const { apiKey, apiSecret } = useContext(AuthContext);
  const postNewSigningKey = useCallback(
    async (
      acc: string,
      registerMessage: IRegisterMessage,
      accountSignature: string,
      signingKeySignature: string,
      signingKey: string,
      refCode?: string,
      inviteCode?: string,
      // Number of times to retry. If not provided, dont retry
      retryCount?: number
    ): Promise<IRegisterAuthInfoResponse> => {
      try {
        const response = (
          await (
            await fetcher.postRegister({
              account: acc,
              signing_key: registerMessage.key,
              expiry: registerMessage.expiry,
              account_signature: ethers.utils.joinSignature(
                ethers.utils.splitSignature(accountSignature)
              ),
              signing_key_signature: signingKeySignature,
              referral_code: refCode,
              invite_code: inviteCode,
              no_api_key: Boolean(apiKey && apiSecret), // do not create new api key and secret if both are are defined
            })
          )()
        ).data;

        const api_key = response.api_key || apiKey;
        const api_secret = response.api_secret || apiSecret;

        if (api_key && api_secret) {
          const authInfo: IAuthInfo = {
            signingKey,
            account: acc,
            apiKey: api_key,
            apiSecret: api_secret,
            expiry: registerMessage.expiry,
            encrypted: false,
            passwordProtected: false,
          };

          // Before enabling trading, confirm the new key works with an
          // authenticated read (GET /account), retrying while it propagates.
          const newFetcher = authApi(api_key, api_secret);
          const success = await verifyNewApiKey(async () =>
            (await newFetcher.getAccount())()
          );

          if (success) {
            return {
              authInfo,
              error: undefined,
            };
          }
          // if for some reason api returns 200 but no api keys and signing keys available
          Sentry.captureException(
            Error(
              "POST register returned api keys, but /account is still returning 401"
            ),
            {
              extra: {
                response: JSON.stringify({
                  account: acc,
                  response,
                }),
              },
            }
          );
          return {
            authInfo: undefined,
            error: "Invalid signing key, please re-enable trading",
          };
        }
        // Delete auth info and show error
        // if for some reason api returns 200 but no api keys and signing keys available
        Sentry.captureException(Error("POST register not returning api keys"), {
          extra: {
            response: JSON.stringify(response),
          },
        });
        return {
          authInfo: undefined,
          error: "Invalid signing key, please re-enable trading",
        };
      } catch (error: any) {
        // eslint-disable-next-line no-console
        logger.error(error);
        if (error.code === ChainCodeErrorEnum.CANCELLED) {
          return {
            authInfo: undefined,
            error: "Signature request cancelled. Please try again.",
          };
        }

        // If rate limited, retry with the retry_after response, or retry 1 second later
        if ((error as AxiosError).response?.status === 429) {
          if (retryCount && retryCount > 0) {
            const retryAfter = error.response?.data?.retry_after;
            const retryAfterMillis = retryAfter
              ? nanosToMillis(retryAfter)
              : 1000;
            await delay(retryAfterMillis);

            return postNewSigningKey(
              acc,
              registerMessage,
              accountSignature,
              signingKeySignature,
              signingKey,
              refCode,
              inviteCode,
              retryCount - 1
            );
          }
        }
        const responseError = (error as AxiosError<any>).response?.data?.error;
        return {
          authInfo: undefined,
          error: responseError || "Unable to register signing key. Please try again.",
        };
      }
    },
    [apiKey, apiSecret, fetcher]
  );

  const registerSigningKey = useCallback(
    async (
      isRememberMeChecked: boolean,
      inviteCode?: string
    ): Promise<{
      status: ToastStatusEnum;
      message: string;
    }> => {
      const normalizedAccount = account?.toLowerCase();
      if (
        registerSigningKeyInFlightRef.current ||
        (normalizedAccount &&
          signingKeyRegistrationInFlightAccounts.has(normalizedAccount))
      ) {
        return {
          status: ToastStatusEnum.ERROR,
          message:
            "Signing already in progress. Please confirm or reject the open wallet request.",
        };
      }

      try {
        if (!account) {
          throw Error("Unauthenticated");
        }

        registerSigningKeyInFlightRef.current = true;
        signingKeyRegistrationInFlightAccounts.add(account.toLowerCase());

        // Register under the chain the ORDERS will be signed under
        // (useOrderActions signs with `supportedChainId`), not the wallet's
        // live chain. The EIP-712 domain takes both `name` and `chainId` from
        // this value, so any divergence mints a key whose signatures the
        // backend can never verify — and nothing downstream detects it.
        const chainId = supportedChainId;

        // Ask activeSigner for the provider, exactly as every other wallet-write
        // path does. This was one of TWO still taking `useWallet().provider`,
        // which is web3-react's PRIORITY connector: `findIndex(isActive)` over
        // `allConnectors`, where `metaMask` (bound to window.ethereum) sits at
        // index 0 and `eip6963Connector` -- the wallet the user actually picked
        // -- sits at index 3. First-active-wins, so on a machine with a second
        // extension the signature went to a wallet nobody selected, which had
        // never been granted this account: EIP-1193 4100.
        //
        // `@web3-react/metamask` has no deactivate(), so its listeners stay bound
        // to window.ethereum for the life of the page and can re-activate that
        // store mid-session -- the divergence is not even stable.
        //
        // The other was DesktopPWAModal's signing-key registration, fixed the same
        // way in the same change -- it additionally took its EIP-712 `chainId`
        // from `provider.getNetwork()`, so a wallet on another chain minted a key
        // the backend could never verify.
        //
        // getWeb3ProviderForChain also replaces the hand-rolled check that used
        // to live here: it asks the provider directly for eth_chainId rather than
        // trusting web3-react's cached value, which is precisely what goes stale
        // when a provider is swapped underneath.
        // ONE source for both. The domain above takes `supportedChainId`, so the
        // guard must assert the wallet is on THAT chain -- not on whatever
        // getCurrentChainConfig() resolves to. They agree today (both derive from
        // getChainIdFromEnv), but getCurrentChainConfig() silently falls back to
        // SEPOLIA for an unconfigured chain id, which would have the guard demand
        // Sepolia while the key was minted for the env chain.
        const provider = await getWeb3ProviderForChain(
          getChainConfigOrThrow(chainId)
        );

        const expiry = moment()
          .add(
            isRememberMeChecked
              ? { days: SIGNING_KEY_TTL.remembered.days }
              : { hours: SIGNING_KEY_TTL.session.hours }
          )
          .unix()
          .toString();

        const signingKeyData = await createSigningKey({
          provider,
          account,
          chainId,
          expiry,
        });

        const { authInfo, error } = await postNewSigningKey(
          account,
          signingKeyData.registerMessage,
          signingKeyData.accountSignature,
          signingKeyData.signingKeySignature,
          signingKeyData.privateKey,
          referralCode || undefined,
          inviteCode
        );

        if (error || !authInfo) {
          return {
            status: ToastStatusEnum.ERROR,
            message: error,
          };
        }
        enableTrading(authInfo, isRememberMeChecked);
        return {
          status: ToastStatusEnum.SUCCESS,
          message: "Signing key registered successfully",
        };
      } catch (error: any) {
        // eslint-disable-next-line no-console
        logger.error(error);
        return {
          status: ToastStatusEnum.ERROR,
          message: registerSigningKeyErrorMessage(error),
        };
      } finally {
        registerSigningKeyInFlightRef.current = false;
        if (normalizedAccount) {
          signingKeyRegistrationInFlightAccounts.delete(normalizedAccount);
        }
      }
    },
    [account, enableTrading, postNewSigningKey, referralCode]
  );

  return {
    registerSigningKey,
    postNewSigningKey,
  };
};
